import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ScopedUser, toFlatRoles } from '../../shared/utils/legacy-user-roles.util';

/**
 * The user service no longer returns the flat `roles` list of a user but the country- and
 * stream-scoped `userRoles`. Every user that is delivered through one of the user endpoints
 * gets its flat role list restored here, so that the role checks throughout the application
 * keep working without touching every single one of them.
 */
@Injectable()
export class UserRolesInterceptor implements HttpInterceptor {
    private readonly userEndpoints = [`${environment.apiUrl}/me`, `${environment.apiUrl}/users`];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.userEndpoints.some((endpoint) => request.url.startsWith(endpoint))) {
            return next.handle(request);
        }

        return next
            .handle(request)
            .pipe(
                map((event) =>
                    event instanceof HttpResponse && event.body
                        ? event.clone({ body: this.addFlatRoles(event.body) })
                        : event
                )
            );
    }

    private addFlatRoles(body: unknown): unknown {
        if (Array.isArray(body)) {
            return body.map((entry) => this.addFlatRoles(entry));
        }

        if (!body || typeof body !== 'object') {
            return body;
        }

        const entries = Object.entries(body as Record<string, unknown>).map(([key, value]) => [
            key,
            this.addFlatRoles(value)
        ]);
        const mapped = Object.fromEntries(entries) as Record<string, unknown>;

        return this.isUser(mapped) ? { ...mapped, roles: toFlatRoles(mapped as ScopedUser) } : mapped;
    }

    private isUser(candidate: Record<string, unknown>): boolean {
        return 'gid' in candidate && ('userRoles' in candidate || 'isInternal' in candidate);
    }
}
